import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Language } from '../data/types/LanguageTypes';
import translations from '../assets/features/Translations';

// Type for nested translation access
type TranslationPath = {
    [K in keyof typeof translations]: {
        [P in keyof typeof translations[K]]: string;
    };
};

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    getTranslation: (key: string) => string;
    t: ((key: string) => string) & TranslationPath; // Supports both string and nested access
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Clean up old localStorage keys on mount
    useEffect(() => {
        // Remove old duplicate keys
        if (localStorage.getItem('preferredLanguage')) {
            localStorage.removeItem('preferredLanguage');
        }
    }, []);

    // Get language from localStorage or default to 'pt'
    const getInitialLanguage = (): Language => {
        const saved = localStorage.getItem('languagePreference');
        if (saved === 'en' || saved === 'pt') {
            return saved as Language;
        }
        return 'pt';
    };

    const [language, setLanguageState] = useState<Language>(getInitialLanguage);

    const setLanguage = useCallback((newLanguage: Language) => {
        setLanguageState(newLanguage);
        
        // Save to localStorage directly
        localStorage.setItem('languagePreference', newLanguage);
        
        // Update HTML lang attribute
        document.documentElement.lang = newLanguage;
        
        // Dispatch custom event for other components
        window.dispatchEvent(new CustomEvent('languageChanged', {
            detail: { language: newLanguage }
        }));
    }, []);

    useEffect(() => {
        // Initialize language from localStorage on mount
        const savedLanguage = localStorage.getItem('languagePreference');
        if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'pt')) {
            setLanguageState(savedLanguage as Language);
            document.documentElement.lang = savedLanguage;
        } else {
            // Set default to Portuguese if no language is saved
            const defaultLanguage: Language = 'pt';
            setLanguageState(defaultLanguage);
            localStorage.setItem('languagePreference', defaultLanguage);
            document.documentElement.lang = defaultLanguage;
        }

        // Expose language context to window for scripts
        (window as any).LanguageContext = {
            getCurrentLanguage: () => language
        };
    }, [language]);

    // Get translation for a specific key
    const getTranslation = useCallback((key: string): string => {
        // Navigate through nested translation object
        const keys = key.split('.');
        let value: any = translations;
        
        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                console.warn(`Translation key not found: ${key}`);
                return key; // Return key if translation not found
            }
        }
        
        // Check if we have a translation object with en/pt
        if (value && typeof value === 'object' && ('en' in value || 'pt' in value)) {
            return value[language] || value['en'] || value['pt'] || key;
        }
        
        return key;
    }, [language]);

    // Create a Proxy-based translation function that supports both string and nested access
    const t = useMemo(() => {
        // Base function for string access: t('NAVIGATION.budget')
        const baseFunction = (key: string): string => {
        return getTranslation(key);
        };

        // Helper function to create nested proxies for deep access
        const createNestedProxy = (path: string[]): any => {
            return new Proxy({}, {
                get: (_target, prop: string | symbol) => {
                    // Ignore React internal properties and Symbols
                    if (typeof prop === 'symbol' || prop.startsWith('$$') || prop === 'constructor' || prop === 'prototype') {
                        return undefined;
                    }
                    
                    const newPath = [...path, prop];
                    const keys = newPath;
                    let value: any = translations;
                    
                    // Navigate through the translation object
                    for (const k of keys) {
                        if (value && typeof value === 'object' && k in value) {
                            value = value[k];
                        } else {
                            // Only warn for actual translation keys, not React internals
                            const propStr = typeof prop === 'string' ? prop : String(prop);
                            if (!propStr.startsWith('$$') && !propStr.startsWith('@@') && propStr !== 'then') {
                            console.warn(`Translation key not found: ${newPath.join('.')}`);
                            }
                            return newPath.join('.');
                        }
                    }
                    
                    // If we have a translation object with en/pt, return the translated string
                    if (value && typeof value === 'object' && ('en' in value || 'pt' in value)) {
                        return value[language] || value['en'] || value['pt'] || newPath.join('.');
                    }
                    
                    // If it's a primitive value (string, number, boolean), return it directly
                    if (value !== null && value !== undefined && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
                        return value;
                    }
                    
                    // If it's still an object, create another nested proxy
                    if (value && typeof value === 'object') {
                        return createNestedProxy(newPath);
                    }
                    
                    return newPath.join('.');
                }
            });
        };

        // Create a Proxy to enable nested access: t.NAVIGATION.budget or t.HERO.slider1.subtitle
        return new Proxy(baseFunction, {
            get: (_target, prop: string | symbol) => {
                // Ignore React internal properties, Symbols, and other special properties
                if (typeof prop === 'symbol' || 
                    prop.startsWith('$$') || 
                    prop.startsWith('@@') ||
                    prop === 'constructor' || 
                    prop === 'prototype' ||
                    prop === 'then' ||
                    prop === 'catch' ||
                    prop === 'finally') {
                    return undefined;
                }
                
                if (prop in translations) {
                    // Return a nested proxy for the section
                    return createNestedProxy([prop]);
                }
                return undefined;
            }
        }) as ((key: string) => string) & TranslationPath;
    }, [language, getTranslation]);

    const contextValue: LanguageContextType = {
        language,
        setLanguage,
        getTranslation,
        t
    };

    return (
        <LanguageContext.Provider value={contextValue}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
